import type { Camera } from '@babylonjs/core/Cameras/camera';
import { Effect } from '@babylonjs/core/Materials/effect';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { RenderTargetTexture } from '@babylonjs/core/Materials/Textures/renderTargetTexture';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { PostProcess } from '@babylonjs/core/PostProcesses/postProcess';
import type { Scene } from '@babylonjs/core/scene';

import { renderFeatures } from './compat';

/**
 * The hover outline: a crisp line around the **silhouette** of whatever is under the cursor, drawn
 * on the screen rather than on the meshes.
 *
 * ── Why not `renderOutline` ─────────────────────────────────────────────────────────────
 *
 * Babylon's mesh outline draws each mesh again, inflated, behind itself. A monster is a couple of
 * dozen parts on joints, so it got a couple of dozen shells, overlapping where the parts meet; a
 * painted body or a generated prop got a swollen copy of itself. The owner (2026-09-27): *the
 * overlay looks weird over the 3D models — could it just outline the actual shapes and have more
 * of a 2D look?*
 *
 * So the hovered meshes are rendered, flat white, into a mask; one full-screen pass then draws the
 * outline colour where the mask is **absent but a neighbour within `OUTLINE_PX` has it** — the
 * silhouette's edge, one line, nothing inside it, the way a 2D sprite outline looks. It is drawn
 * over the scene, not depth-tested: a hovered monster behind a pillar still shows where it is.
 *
 * `architecture/ENTITY-MODELS-ANIMATION.md` holds the contract. The rule every pass here follows
 * applies: its shader is registered from this module, and it detaches itself if it fails to compile
 * — after which every caller falls back to `renderOutline` (`highlightParts`).
 */

/** Line width, in CSS pixels. Chosen by looking: 1 vanished on a moving figure, 3 read as a halo. */
export const OUTLINE_PX = 2;
export const OUTLINE_COLOUR = new Color3(0.95, 0.8, 0.45);
/**
 * How far, in outline widths, a pixel looks both ways to decide it is a hole inside the silhouette
 * rather than the world outside it: three of four axes with the model at both ends and it draws no
 * line. Chosen: 4 widths closes the speckled gaps a generated mesh leaves (reported: *make the
 * overlay only outside the models and not inside it*) and is still narrower than the gap between a
 * character's legs, which stays outlined.
 */
export const ENCLOSED_REACH = 4;

const SHADER = 'grindSilhouetteOutline';
Effect.ShadersStore[`${SHADER}FragmentShader`] = `
  precision highp float;
  varying vec2 vUV;
  uniform sampler2D textureSampler;
  uniform sampler2D maskSampler;
  // xy: one pixel in UV, z: line width in pixels, w: opacity
  uniform vec4 outlineParams;
  uniform vec3 outlineColour;

  void main(void) {
    vec4 scene = texture2D(textureSampler, vUV);
    // Nothing hovered: one read and out, rather than sixteen per pixel for an empty mask.
    if (outlineParams.w < 0.5 || texture2D(maskSampler, vUV).r > 0.5) {
      gl_FragColor = scene;
      return;
    }
    // Eight directions at the full width and at half of it, so a thin limb is not skipped over.
    float edge = 0.0;
    for (int i = 0; i < 8; i++) {
      float a = float(i) * 0.785398;
      vec2 dir = vec2(cos(a), sin(a)) * outlineParams.xy;
      edge = max(edge, texture2D(maskSampler, vUV + dir * outlineParams.z).r);
      edge = max(edge, texture2D(maskSampler, vUV + dir * outlineParams.z * 0.5).r);
    }
    // A hole *inside* the silhouette — a gap in a generated mesh, a sliver between two parts — has
    // the model on both sides along most directions; an edge of the silhouette never does, because
    // one side of it is the world. So an enclosed pixel draws no line: only the outside is outlined.
    float enclosed = 0.0;
    for (int i = 0; i < 4; i++) {
      float a = float(i) * 0.785398;
      vec2 dir = vec2(cos(a), sin(a)) * outlineParams.xy * outlineParams.z * ${ENCLOSED_REACH.toFixed(1)};
      float both = step(0.5, texture2D(maskSampler, vUV + dir).r) * step(0.5, texture2D(maskSampler, vUV - dir).r);
      enclosed += both;
    }
    edge *= 1.0 - step(2.5, enclosed);
    gl_FragColor = vec4(mix(scene.rgb, outlineColour, edge * outlineParams.w), scene.a);
  }
`;

/** Tells a caller which outline to use. Null: fall back to `renderOutline`. */
let active: SilhouetteOutline | null = null;

/** The silhouette outline, when it is running. */
export function silhouetteOutline(): SilhouetteOutline | null {
  return active;
}

/**
 * Outlines `parts` (or clears them) — through the silhouette pass when it runs, through
 * `renderOutline` when it does not. `owner` keys the set, so one caller clearing its outline never
 * clears another's. The one call every hover site makes.
 */
export function highlightParts(
  owner: string,
  parts: readonly AbstractMesh[],
  on: boolean,
  fallbackWidth: number,
  colour: Color3 = OUTLINE_COLOUR,
): void {
  if (active) {
    active.set(owner, on ? parts : null);
    return;
  }
  for (const part of parts) {
    part.renderOutline = on;
    if (on) {
      part.outlineWidth = fallbackWidth;
      part.outlineColor = colour;
    }
  }
}

export class SilhouetteOutline {
  readonly mask: RenderTargetTexture;
  readonly pass: PostProcess;
  private readonly flat: StandardMaterial;
  private readonly owners = new Map<string, readonly AbstractMesh[]>();

  constructor(
    private readonly scene: Scene,
    private readonly camera: Camera,
  ) {
    const engine = scene.getEngine();
    // Full resolution, or a two-pixel line would be one blurred pixel wide.
    this.mask = new RenderTargetTexture('grind-outline-mask', { ratio: 1 }, scene, false);
    this.mask.clearColor = new Color4(0, 0, 0, 0);
    this.mask.renderList = [];
    this.mask.activeCamera = camera;
    scene.customRenderTargets.push(this.mask);

    // Flat, unlit, white: the mask needs a shape, not a picture. A `StandardMaterial`, so a skinned
    // body deforms in the mask exactly as it does on screen.
    this.flat = new StandardMaterial('grind-outline-flat', scene);
    this.flat.disableLighting = true;
    this.flat.emissiveColor = new Color3(1, 1, 1);
    this.flat.diffuseColor = new Color3(0, 0, 0);
    this.flat.specularColor = new Color3(0, 0, 0);
    this.flat.fogEnabled = false;
    // Both faces: a one-sided plane seen from behind (a portal's membrane) is not drawn into the
    // mask otherwise, and the hole it leaves inside the silhouette was outlined.
    this.flat.backFaceCulling = false;

    this.pass = new PostProcess('grind-outline', SHADER, ['outlineParams', 'outlineColour'], ['maskSampler'], 1.0, camera);
    // It is first in the chain, so it receives the scene render: ask for MSAA, as the grade does.
    this.pass.samples = 4;
    this.pass.onApply = (effect) => {
      const w = engine.getRenderWidth();
      const h = engine.getRenderHeight();
      const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
      effect.setTexture('maskSampler', this.mask);
      effect.setFloat4('outlineParams', 1 / w, 1 / h, OUTLINE_PX * dpr, this.owners.size > 0 ? 1 : 0);
      effect.setColor3('outlineColour', OUTLINE_COLOUR);
    };
    this.pass.onEffectCreatedObservable.add((effect) => {
      effect.onError = (_e, errors) => {
        console.error('grind: outline shader failed to compile, falling back to mesh outlines\n', errors);
        this.dispose();
      };
    });
    active = this;
  }

  /** Sets the meshes one owner wants outlined, or clears them with null. */
  set(owner: string, parts: readonly AbstractMesh[] | null): void {
    if (parts && parts.length > 0) this.owners.set(owner, parts);
    else this.owners.delete(owner);
    const list = [...new Set([...this.owners.values()].flat())];
    this.mask.renderList = list;
    for (const mesh of list) this.mask.setMaterialForRendering(mesh, this.flat);
    // Nothing hovered: the mask need not be redrawn every frame.
    this.mask.refreshRate = list.length > 0 ? 1 : RenderTargetTexture.REFRESHRATE_RENDER_ONCE;
    if (list.length === 0) this.mask.resetRefreshCounter();
  }

  /** The meshes currently outlined, for tests and the diagnostics overlay. */
  get outlined(): readonly AbstractMesh[] {
    return this.mask.renderList ?? [];
  }

  dispose(): void {
    if (active === this) active = null;
    const at = this.scene.customRenderTargets.indexOf(this.mask);
    if (at !== -1) this.scene.customRenderTargets.splice(at, 1);
    this.pass.dispose(this.camera);
    this.mask.dispose();
    this.flat.dispose();
  }
}

/** Builds the outline when this environment can run it. Safe mode and `?outline=0` fall back. */
export function attachSilhouetteOutline(scene: Scene, camera: Camera): SilhouetteOutline | null {
  if (!renderFeatures.outline) return null;
  return new SilhouetteOutline(scene, camera);
}
